import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Upload,
  X,
} from "lucide-react";

const INPUT_TYPES = [
  "TEXT",
  "NUMBER",
  "RADIO",
  "CHECKBOX",
  "MULTI_SELECT",
  "DROPDOWN",
];
const HAS_OPTIONS = ["RADIO", "CHECKBOX", "MULTI_SELECT", "DROPDOWN"];

const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const newField = () => ({
  id: Date.now(),
  label: "",
  key: "",
  inputType: "TEXT",
  isRequired: false,
  isMatchable: false,
  helpText: "",
  options: [],
  _expanded: true,
});

const newOption = () => ({ id: Date.now(), label: "", value: "" });

const CreateCategory = () => {
  const navigate = useNavigate();
  const { createServiceCategory } = useContext(AdminContext);

  const [form, setForm] = useState({
    name: "",
    label: "",
    labelAmh: "",
    description: "",
    descriptionAmh: "",
    color: "#CE257A",
    defaultFee: "",
  });
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // Image upload states
  const [iconImage, setIconImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Image upload handlers
  const handleIconImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setIconImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setIconPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeIconImage = () => {
    setIconImage(null);
    setIconPreview(null);
  };

  const removeCoverImage = () => {
    setCoverImage(null);
    setCoverPreview(null);
  };

  /* ---------- Field helpers ---------- */
  const addField = () => setFields((f) => [...f, newField()]);
  const removeField = (fid) => setFields((f) => f.filter((x) => x.id !== fid));
  const updateField = (fid, k, v) =>
    setFields((f) => f.map((x) => (x.id === fid ? { ...x, [k]: v } : x)));
  const toggleExpand = (fid) =>
    setFields((f) =>
      f.map((x) => (x.id === fid ? { ...x, _expanded: !x._expanded } : x)),
    );

  /* ---------- Option helpers ---------- */
  const addOption = (fid) =>
    setFields((f) =>
      f.map((x) =>
        x.id === fid ? { ...x, options: [...x.options, newOption()] } : x,
      ),
    );
  const removeOption = (fid, oid) =>
    setFields((f) =>
      f.map((x) =>
        x.id === fid
          ? { ...x, options: x.options.filter((o) => o.id !== oid) }
          : x,
      ),
    );
  const updateOption = (fid, oid, k, v) =>
    setFields((f) =>
      f.map((x) =>
        x.id === fid
          ? {
              ...x,
              options: x.options.map((o) =>
                o.id === oid
                  ? {
                      ...o,
                      [k]: v,
                      ...(k === "label" ? { value: slugify(v) } : {}),
                    }
                  : o,
              ),
            }
          : x,
      ),
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.label) {
      toast.error("Name and label are required");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();

      // Add basic fields
      formData.append("name", form.name);
      formData.append("label", form.label);
      if (form.labelAmh) formData.append("labelAmh", form.labelAmh);
      if (form.description) formData.append("description", form.description);
      if (form.descriptionAmh) formData.append("descriptionAmh", form.descriptionAmh);
      formData.append("color", form.color);
      if (form.defaultFee) formData.append("defaultFee", form.defaultFee);

      // Add images
      if (iconImage) formData.append("iconImage", iconImage);
      if (coverImage) formData.append("coverImage", coverImage);

      // Add fields as JSON string
      const fieldsPayload = fields.map((f, i) => ({
        label: f.label,
        key: f.key || slugify(f.label),
        inputType: f.inputType,
        isRequired: f.isRequired,
        isMatchable: f.isMatchable,
        helpText: f.helpText,
        sortOrder: i,
        options: HAS_OPTIONS.includes(f.inputType)
          ? f.options.map((o, oi) => ({
              label: o.label,
              value: o.value || slugify(o.label),
              sortOrder: oi,
            }))
          : [],
      }));
      formData.append("fields", JSON.stringify(fieldsPayload));

      const data = await createServiceCategory(formData);
      if (data.success) {
        navigate("/categories");
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={() => navigate("/categories")}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Create Category
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Define a new service category with custom dynamic fields
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
            Basic Info
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                System Key <span className="text-red-500">*</span>
              </label>
              <input
                value={form.name}
                onChange={(e) => updateForm("name", slugify(e.target.value))}
                placeholder="e.g. childcare"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="text-xs text-gray-400 mt-1">
                Lowercase, underscores only. Cannot change after creation.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Label (EN) <span className="text-red-500">*</span>
              </label>
              <input
                value={form.label}
                onChange={(e) => updateForm("label", e.target.value)}
                placeholder="e.g. Childcare"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Label (AM)
              </label>
              <input
                value={form.labelAmh}
                onChange={(e) => updateForm("labelAmh", e.target.value)}
                placeholder="e.g. የሕፃናት እንክብካቤ"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Default Fee (Br)
              </label>
              <input
                type="number"
                value={form.defaultFee}
                onChange={(e) => updateForm("defaultFee", e.target.value)}
                placeholder="0.00"
                min="0"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Description (EN)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => updateForm("description", e.target.value)}
              rows={2}
              placeholder="Brief description of this service category"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Description (AM)
            </label>
            <textarea
              value={form.descriptionAmh}
              onChange={(e) => updateForm("descriptionAmh", e.target.value)}
              rows={2}
              placeholder="የአገልግሎት ምድብ አጭር መግለጫ"
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Color
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.color}
                onChange={(e) => updateForm("color", e.target.value)}
                className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                {form.color}
              </span>
            </div>
          </div>

          {/* Icon Image Upload */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Icon Image <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Professional icon for the category (recommended: 256x256px, max
              5MB)
            </p>
            {iconPreview ? (
              <div className="relative inline-block">
                <img
                  src={iconPreview}
                  alt="Icon preview"
                  className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={removeIconImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Click to upload icon image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleIconImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Cover Image Upload */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Cover Image
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Cover image for frontend display (recommended: 1200x400px, max
              5MB)
            </p>
            {coverPreview ? (
              <div className="relative inline-block w-full">
                <img
                  src={coverPreview}
                  alt="Cover preview"
                  className="w-full h-40 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={removeCoverImage}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Click to upload cover image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Field builder */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Custom Fields
            </h2>
            <button
              type="button"
              onClick={addField}
              className="flex items-center gap-1.5 text-primary text-sm font-medium hover:underline"
            >
              <Plus size={14} /> Add Field
            </button>
          </div>

          {fields.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              <p className="text-sm text-gray-400">
                No fields yet. Click Add Field to define custom inputs for this
                category.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {fields.map((field, idx) => (
                <div
                  key={field.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                >
                  {/* Field header */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-900/40">
                    <GripVertical
                      size={14}
                      className="text-gray-300 cursor-grab"
                    />
                    <span className="text-xs font-mono text-gray-400 w-5">
                      {idx + 1}
                    </span>
                    <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                      {field.label || (
                        <span className="text-gray-400 italic">
                          Unnamed field
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                      {field.inputType}
                    </span>
                    {field.isRequired && (
                      <span className="text-xs text-red-500 font-medium">
                        Required
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleExpand(field.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {field._expanded ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeField(field.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Field body */}
                  {field._expanded && (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Label <span className="text-red-500">*</span>
                          </label>
                          <input
                            value={field.label}
                            onChange={(e) => {
                              updateField(field.id, "label", e.target.value);
                              updateField(
                                field.id,
                                "key",
                                slugify(e.target.value),
                              );
                            }}
                            placeholder="Field label"
                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Key
                          </label>
                          <input
                            value={field.key}
                            onChange={(e) =>
                              updateField(
                                field.id,
                                "key",
                                slugify(e.target.value),
                              )
                            }
                            placeholder="auto_generated"
                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm font-mono text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Input Type
                          </label>
                          <select
                            value={field.inputType}
                            onChange={(e) =>
                              updateField(field.id, "inputType", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            {INPUT_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-end gap-4 pb-0.5">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.isRequired}
                              onChange={(e) =>
                                updateField(
                                  field.id,
                                  "isRequired",
                                  e.target.checked,
                                )
                              }
                              className="rounded text-primary focus:ring-primary"
                            />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Required
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.isMatchable}
                              onChange={(e) =>
                                updateField(
                                  field.id,
                                  "isMatchable",
                                  e.target.checked,
                                )
                              }
                              className="rounded text-primary focus:ring-primary"
                            />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Matchable
                            </span>
                          </label>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Help Text
                          </label>
                          <input
                            value={field.helpText}
                            onChange={(e) =>
                              updateField(field.id, "helpText", e.target.value)
                            }
                            placeholder="Optional hint"
                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                        </div>
                      </div>

                      {/* Options builder */}
                      {HAS_OPTIONS.includes(field.inputType) && (
                        <div className="pt-2">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                              Options
                            </label>
                            <button
                              type="button"
                              onClick={() => addOption(field.id)}
                              className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                            >
                              <Plus size={11} /> Add Option
                            </button>
                          </div>
                          {field.options.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">
                              No options yet — add at least one.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {field.options.map((opt) => (
                                <div
                                  key={opt.id}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    value={opt.label}
                                    onChange={(e) =>
                                      updateOption(
                                        field.id,
                                        opt.id,
                                        "label",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Label"
                                    className="flex-1 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  />
                                  <input
                                    value={opt.value}
                                    onChange={(e) =>
                                      updateOption(
                                        field.id,
                                        opt.id,
                                        "value",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="value"
                                    className="w-32 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent text-sm font-mono text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeOption(field.id, opt.id)
                                    }
                                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex gap-3 pb-8">
          <button
            type="button"
            onClick={() => navigate("/categories")}
            className="flex-1 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {saving
              ? "Creating..."
              : `Create Category${fields.length > 0 ? ` (${fields.length} field${fields.length > 1 ? "s" : ""})` : ""}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCategory;
